// ─────────────────────────────────────────────────────────────────────────────
// okrPlanCoachChatStore — the coach's OKR Plans chats on the shared
// `/new-chat` namespace in COACH MODE (BE_HANDOFF.md §2.8, §2.9; CD23).
//
// The coach app has no shared chat store for `/new-chat`, so this one keeps
// what `ProjectChat` needs per chat (messages, thinking, streaming) plus the
// `okr_plan_*` cards and pushes. Listeners are bound once per connection and
// route every event by `chat_id`; a chat registers what it belongs to
// (`openChat(chatId, { planId, reportId })`) so a saved remark or a new final
// summary lands on the right report / review panel.
// ─────────────────────────────────────────────────────────────────────────────

import { createStore } from "zustand/vanilla";
import { useEffect, useState } from "react";
import { unifiedProjectSocketManager } from "../../../../socket";
import okrPlansCoachStore from "../_store/okrPlansCoachStore";

const NAMESPACE = "new-chat";

export const COACH_CHAT_EMIT = {
  GET_CHAT_MESSAGES: "get_chat_messages",
  SEND_MESSAGE: "send_message",
  CANCEL_MESSAGE: "cancel_message",
  GET_BOARD: "okr_plan_get_board",
  REMARK_SAVE: "okr_plan_remark_save",
  REMARK_KEEP_EDITING: "okr_plan_remark_keep_editing",
  FINAL_CHANGE_CONFIRM: "okr_plan_final_change_confirm",
  FINAL_CHANGE_CANCEL: "okr_plan_final_change_cancel",
};

export const COACH_CHAT_ON = {
  CHAT_DETAILS: "chat_details",
  SHOW_THINKING: "show_thinking",
  AGENT_STREAM: "agent_stream",
  MESSAGE_RECEIVED: "message_recieved", // backend spelling
  WORKFLOW_DONE: "workflow_done",
  STREAM_STOPPED: "stream_stopped",
  TOKENS_EXHAUSTED: "tokens_exhausted",
  ERROR: "error",
  BOARD: "okr_plan_board",
  CHIPS: "okr_plan_chips",
  REMARK_CARD: "okr_plan_remark_card",
  REMARK_CARD_UPDATED: "okr_plan_remark_card_updated",
  REMARK_SAVED: "okr_plan_remark_saved",
  SUMMARY_CHANGE: "okr_plan_summary_change",
  SUMMARY_CHANGE_UPDATED: "okr_plan_summary_change_updated",
  FINAL_SUMMARY: "okr_plan_final_summary",
  CARD_ERROR: "qokr_error",
};

// The `sender` cards carry in the stream (the event that brought them).
export const COACH_CARD = {
  REMARK: COACH_CHAT_ON.REMARK_CARD,
  SUMMARY_CHANGE: COACH_CHAT_ON.SUMMARY_CHANGE,
};

const EMPTY_CHAT = {
  messages: [],
  messageLoading: true,
  loading: false,
  streamLoading: false,
  streamMessage: "",
  error: null,
  board: null,
  chips: null,
  cardBusy: {},
  cardErrors: {},
};

const parse = (raw) => {
  try {
    return typeof raw === "string" ? JSON.parse(raw) : raw;
  } catch {
    return null;
  }
};

// A history row may hold its card JSON in `message` (BE D106).
export const parseCardRow = (row) => {
  if (!row || typeof row.message !== "string") return row;
  const stored = parse(row.message);
  if (!stored || typeof stored !== "object") return row;
  const rest = { ...row };
  delete rest.message;
  return { ...stored, ...rest };
};

// Which card a row is: live sender, or (history) its fields.
export const coachCardKind = (row) => {
  if (!row) return null;
  if (Object.values(COACH_CARD).includes(row.sender)) return row.sender;
  if (typeof row.sender !== "string" || !row.sender.startsWith("okr_plan"))
    return null;
  const card = parseCardRow(row);
  if (card?.remark != null) return COACH_CARD.REMARK;
  if (card?.field) return COACH_CARD.SUMMARY_CHANGE;
  return null;
};

// ProjectChat expects `files` on every user row.
// Rows reloaded from history carry `chat_message_id` instead of
// `message_id`; every card action sends `card_id` = this id (CD37).
const normalise = (m) => ({
  files: [],
  ...m,
  message_id: m.message_id ?? m.chat_message_id,
});

const okrPlanCoachChatStore = createStore((set, get) => {
  const chat = (id) => get().chats[id] || EMPTY_CHAT;
  const update = (id, fields) =>
    set((s) => {
      const current = s.chats[id] || EMPTY_CHAT;
      const next = typeof fields === "function" ? fields(current) : fields;
      return { chats: { ...s.chats, [id]: { ...current, ...next } } };
    });
  const forChat = (handle) => (raw) => {
    const data = parse(raw);
    const id = data && String(data.chat_id ?? data.chat_unique_id ?? "");
    if (id && get().chats[id]) handle(id, data);
  };
  // Text of the last row, for the CD38 same-text checks.
  const lastText = (c) =>
    String(c.messages[c.messages.length - 1]?.message || "").trim();
  const flushStream = (id) =>
    update(id, (c) =>
      // Late tokens can rebuild a reply `message_recieved` already added;
      // flushing them would show it twice (CD38).
      c.streamMessage && c.streamMessage.trim() !== lastText(c)
        ? {
            messages: [
              ...c.messages,
              // `_streamed` lets a `message_recieved` that lands after this
              // flush replace the row instead of repeating it (CD38).
              normalise({
                sender: "assistant",
                message: c.streamMessage,
                _streamed: true,
              }),
            ],
            streamMessage: "",
            streamLoading: false,
          }
        : { streamLoading: false, streamMessage: "" },
    );

  return {
    socket: null,
    bound: false,
    chats: {},
    owners: {}, // { [chatId]: { planId, reportId } }
    tokensExhausted: false,

    connect: () => {
      const socket = unifiedProjectSocketManager.connect(NAMESPACE);
      set({ socket });
      if (!socket || get().bound) return socket;
      set({ bound: true });

      const on = COACH_CHAT_ON;
      socket.on(
        on.CHAT_DETAILS,
        forChat((id, data) =>
          update(id, {
            messages: (data.messages || []).map(normalise),
            messageLoading: false,
            error: null,
          }),
        ),
      );
      socket.on(
        on.SHOW_THINKING,
        forChat((id) => update(id, { loading: true, error: null })),
      );
      socket.on(
        on.AGENT_STREAM,
        forChat((id, data) =>
          update(id, (c) => ({
            loading: false,
            streamLoading: true,
            streamMessage: c.streamMessage + (data.token || ""),
          })),
        ),
      );
      socket.on(
        on.MESSAGE_RECEIVED,
        forChat((id, data) => {
          if (data.sender === "user") return; // already shown optimistically
          update(id, (c) => {
            const row = normalise(data);
            const settled = {
              streamMessage: "",
              streamLoading: false,
              loading: false,
            };
            // A reply can come as streamed tokens AND as this persisted row,
            // in either order. If `workflow_done` beat us here the stream is
            // already a row: swap it for the persisted one rather than show
            // the same text twice (CD38). Same id / same text only, so two
            // different replies are never merged.
            if (
              row.message_id != null &&
              c.messages.some(
                (m) => String(m.message_id) === String(row.message_id),
              )
            )
              return settled;
            const last = c.messages[c.messages.length - 1];
            const sameText =
              String(last?.message || "").trim() ===
              String(row.message || "").trim();
            return {
              ...settled,
              messages:
                sameText &&
                (last?._streamed ||
                  (last?.sender === row.sender && last?.message_id == null))
                  ? [...c.messages.slice(0, -1), row]
                  : [...c.messages, row],
            };
          });
        }),
      );
      socket.on(
        on.WORKFLOW_DONE,
        forChat((id) => {
          flushStream(id);
          update(id, { loading: false });
        }),
      );
      socket.on(
        on.STREAM_STOPPED,
        forChat((id) => {
          flushStream(id);
          update(id, { loading: false });
        }),
      );
      socket.on(on.TOKENS_EXHAUSTED, () => {
        set({ tokensExhausted: true });
        Object.keys(get().chats).forEach((id) =>
          update(id, { loading: false, streamLoading: false }),
        );
      });
      socket.on(
        on.ERROR,
        forChat((id, data) =>
          update(id, {
            loading: false,
            streamLoading: false,
            messageLoading: false,
            error: data.message || "Something went wrong.",
          }),
        ),
      );
      socket.on(
        on.BOARD,
        forChat((id, board) => update(id, { board })),
      );
      socket.on(
        on.CHIPS,
        forChat((id, chips) => update(id, { chips })),
      );
      const pushCard = (sender) =>
        forChat((id, card) =>
          update(id, (c) => {
            const row = normalise({ ...card, sender });
            // The same card twice (e.g. a resend) is shown once (CD38).
            const seen =
              row.message_id != null &&
              c.messages.some(
                (m) => String(m.message_id) === String(row.message_id),
              );
            return {
              messages: seen ? c.messages : [...c.messages, row],
              loading: false,
              streamLoading: false,
              streamMessage: "",
            };
          }),
        );
      const patchCard = forChat((id, { message_id, ...fields }) =>
        update(id, (c) => ({
          messages: c.messages.map((m) =>
            String(m.message_id) === String(message_id)
              ? { ...m, ...fields }
              : m,
          ),
          cardBusy: { ...c.cardBusy, [message_id]: false },
        })),
      );
      socket.on(on.REMARK_CARD, pushCard(COACH_CARD.REMARK));
      socket.on(on.REMARK_CARD_UPDATED, patchCard);
      socket.on(on.SUMMARY_CHANGE, pushCard(COACH_CARD.SUMMARY_CHANGE));
      socket.on(on.SUMMARY_CHANGE_UPDATED, patchCard);
      // The saved remark shows on the report / review panel (BE 2.8, 2.9).
      socket.on(
        on.REMARK_SAVED,
        forChat((id, data) => {
          const owner = get().owners[id] || {};
          const coachRemark = {
            text: data.remark,
            by_name: data.by_name,
            at: data.at,
          };
          const store = okrPlansCoachStore.getState();
          if (data.report_unique_id)
            store.patchReport(data.report_unique_id, {
              coach_remark: coachRemark,
            });
          else if (owner.planId)
            store.patchReview(owner.planId, { coach_remark: coachRemark });
        }),
      );
      socket.on(
        on.FINAL_SUMMARY,
        forChat((id, data) => {
          const owner = get().owners[id] || {};
          const planId = data.plan_unique_id || owner.planId;
          if (planId)
            okrPlansCoachStore
              .getState()
              .patchReview(planId, { summary_final: data.summary_final });
        }),
      );
      socket.on(on.CARD_ERROR, (raw) => {
        const data = parse(raw) || {};
        const cardId = data.card_id || data.message_id;
        Object.keys(get().chats).forEach((id) => {
          const c = chat(id);
          if (
            cardId != null &&
            c.messages.some((m) => String(m.message_id) === String(cardId))
          )
            update(id, {
              cardBusy: { ...c.cardBusy, [cardId]: false },
              cardErrors: {
                ...c.cardErrors,
                [cardId]: data.message || "Something went wrong.",
              },
            });
        });
      });
      // After a reconnect, re-read every open chat.
      socket.on("connect", () =>
        Object.keys(get().chats).forEach((id) =>
          socket.emit(COACH_CHAT_EMIT.GET_CHAT_MESSAGES, { id }),
        ),
      );
      return socket;
    },

    openChat: (chatId, owner = {}) => {
      const id = String(chatId);
      set((s) => ({
        chats: { ...s.chats, [id]: { ...EMPTY_CHAT } },
        owners: { ...s.owners, [id]: owner },
      }));
      const socket = get().connect();
      const load = () =>
        socket?.emit(COACH_CHAT_EMIT.GET_CHAT_MESSAGES, { id });
      if (socket?.connected) load();
      else socket?.once("connect", load);
    },

    closeChat: (chatId) =>
      set((s) => {
        const chats = { ...s.chats };
        const owners = { ...s.owners };
        delete chats[String(chatId)];
        delete owners[String(chatId)];
        return { chats, owners };
      }),

    sendMessage: (chatId, message, featureSlug) => {
      const id = String(chatId);
      const text = (message || "").trim();
      if (!text) return;
      update(id, (c) => ({
        messages: [
          ...c.messages,
          normalise({
            sender: "user",
            message: text,
            created_at: new Date().toISOString(),
          }),
        ],
        chips: null,
        loading: true,
        error: null,
      }));
      get().socket?.emit(COACH_CHAT_EMIT.SEND_MESSAGE, {
        id,
        message: text,
        feature_slug: featureSlug,
      });
    },

    // Card actions: Promise of the ack; busy / error per card.
    cardAction: (chatId, cardId, event, payload = {}) =>
      new Promise((resolve) => {
        const id = String(chatId);
        const socket = get().socket;
        const c = chat(id);
        update(id, {
          cardBusy: { ...c.cardBusy, [cardId]: true },
          cardErrors: { ...c.cardErrors, [cardId]: null },
        });
        if (!socket?.connected) {
          update(id, (x) => ({
            cardBusy: { ...x.cardBusy, [cardId]: false },
            cardErrors: {
              ...x.cardErrors,
              [cardId]: "You're offline. Try again in a moment.",
            },
          }));
          resolve({ acknowledgement: false });
          return;
        }
        socket.emit(
          event,
          { chat_unique_id: id, card_id: cardId, ...payload },
          (ack) => {
            update(id, (x) => ({
              cardBusy: { ...x.cardBusy, [cardId]: false },
              cardErrors: {
                ...x.cardErrors,
                [cardId]: ack?.acknowledgement
                  ? null
                  : ack?.error?.message ||
                    (typeof ack?.error === "string" ? ack.error : null) ||
                    "That didn't go through. Try again.",
              },
            }));
            resolve(ack || { acknowledgement: false });
          },
        );
      }),
  };
});

export const useCoachChat = (chatId) => {
  const pick = () =>
    okrPlanCoachChatStore.getState().chats[String(chatId)] || EMPTY_CHAT;
  const [state, setState] = useState(pick);
  useEffect(() => {
    setState(pick());
    return okrPlanCoachChatStore.subscribe(() => {
      const next = pick();
      setState((prev) => (prev === next ? prev : next));
    });
  }, [chatId]); // eslint-disable-line
  return state;
};

export default okrPlanCoachChatStore;
